﻿import React, { useState, useEffect } from "react";
import { Bell, Trash2 } from "lucide-react";
import TickerPicker from "./TickerPicker.jsx";
import { getStockStats, refreshSummaries, useSummariesVersion } from "../utils/priceData.js";
import { getCompany, useCompanyInfoVersion } from "../utils/companyInfo.js";
import { loadUserData, saveUserData } from "../utils/userStore.js";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function StockAlerts({ user }) {
  useSummariesVersion();
  useCompanyInfoVersion();
  const [alerts, setAlerts] = useState(() => loadUserData("alerts", user, []));
  const [ticker, setTicker] = useState("");
  const [condition, setCondition] = useState("below");
  const [target, setTarget] = useState("");

  const commit = (next) => {
    setAlerts(next);
    saveUserData("alerts", user, next);
  };

  const rows = alerts.map((a) => {
    const current = num(getStockStats(a.ticker).current);
    const hit = current !== null && (a.condition === "below" ? current <= a.target : current >= a.target);
    return { ...a, current, hit };
  });

  // keep prices current while this page is open
  const key = alerts.map((a) => a.ticker).join(",");
  useEffect(() => {
    if (!key) return undefined;
    const list = [...new Set(key.split(","))];
    const id = setInterval(() => refreshSummaries(list), 60000);
    return () => clearInterval(id);
  }, [key]);

  // remember the first time each alert triggered
  const fresh = rows.filter((r) => r.hit && !r.triggeredAt).map((r) => r.id).join(",");
  useEffect(() => {
    if (!fresh) return;
    const ids = fresh.split(",");
    commit(alerts.map((a) => (ids.includes(a.id) ? { ...a, triggeredAt: new Date().toISOString() } : a)));
  }, [fresh]);

  const addAlert = (e) => {
    e.preventDefault();
    const value = parseFloat(target);
    if (!ticker || !(value > 0)) return;
    commit([...alerts, { id: Date.now().toString(36), ticker, condition, target: value }]);
    setTarget("");
  };

  return (
    <main className="page-bg">
      <div className="dash-main-content screens-wrap">
        <h1 className="page-title">Stock Alerts</h1>
        <p className="text-muted mb-4">
          Set a price and see when a stock reaches your buy or sell zone.
        </p>

        <div className="card tool-card mb-4">
          <form className="tool-form" onSubmit={addAlert}>
            <div>
              <label className="tool-label">Company</label>
              <TickerPicker value={ticker} onChange={setTicker} />
            </div>
            <div>
              <label className="tool-label">When price</label>
              <select className="form-select" value={condition} onChange={(e) => setCondition(e.target.value)}>
                <option value="below">falls below</option>
                <option value="above">rises above</option>
              </select>
            </div>
            <div>
              <label className="tool-label">Target price ($)</label>
              <input type="number" min="0" step="0.01" className="form-control" value={target}
                onChange={(e) => setTarget(e.target.value)} placeholder="0.00" required />
            </div>
            <button type="submit" className="btn btn-purple tool-submit" disabled={!ticker}>
              <Bell size={15} /> ADD ALERT
            </button>
          </form>
        </div>

        <div className="card wl-table-card">
          <div className="table-responsive">
            <table className="table wl-table mb-0">
              <thead>
                <tr>
                  <th className="wl-th-plain">Company</th>
                  <th className="wl-th-plain">Condition</th>
                  <th className="wl-th-plain text-end">Target $</th>
                  <th className="wl-th-plain text-end">Current $</th>
                  <th className="wl-th-plain">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const company = getCompany(a.ticker);
                  const done = a.hit || a.triggeredAt;
                  return (
                    <tr key={a.id}>
                      <td>
                        <a className="wl-company" href={`#/stock/${a.ticker}`}>{company ? company.name : a.ticker}</a>
                        <span className="text-muted small ms-2">{a.ticker}</span>
                      </td>
                      <td>{a.condition === "below" ? "Falls below" : "Rises above"}</td>
                      <td className="text-end">{a.target.toFixed(2)}</td>
                      <td className="text-end">{a.current === null ? "—" : a.current.toFixed(2)}</td>
                      <td>
                        <span className={`badge ${done ? "badge-success" : "badge-purple"}`}>
                          {done ? "Triggered" : "Watching"}
                        </span>
                        {a.triggeredAt && (
                          <span className="text-muted small ms-2">{new Date(a.triggeredAt).toLocaleDateString()}</span>
                        )}
                      </td>
                      <td className="text-end">
                        <button className="row-remove" onClick={() => commit(alerts.filter((x) => x.id !== a.id))} aria-label="Delete alert">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {alerts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="wl-empty text-muted">No alerts yet. Create your first one above.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-muted small wl-note">
            Alerts are checked against your database prices (about 15 minutes delayed) whenever this page is open.
            Email or text notifications when the page is closed need a delivery service, which isn't set up yet.
          </p>
        </div>
      </div>
    </main>
  );
}
